<template>
  <div :dir="currentDirection">
    <div class="home-container">
      
      <!-- Hero Section -->
      <!-- Hero Section -->
      <section class="hero bg-noise">
        <!-- Aurora Background Effects & Floating Glass -->
        <div class="aurora-container">
           <div class="aurora-blob blob-1"></div>
           <div class="aurora-blob blob-2"></div>
           <div class="aurora-blob blob-3"></div>
           
           <!-- Floating Glass Objects -->
           <img src="/img/1.webp" class="floating-glass glass-1" alt="" />
           <img src="/img/2.webp" class="floating-glass glass-2" alt="" />
           <img src="/img/3.webp" class="floating-glass glass-3" alt="" />
           <img src="/img/4.webp" class="floating-glass glass-4" alt="" />
        </div>

<div class="container hero-content animate-fade-up text-center">

          <h1 class="display-text text-gradient hero-title">{{ $t('hero_title') }}</h1>
          
          <div class="hero-image-container animate-fade-up delay-200">
              <div class="hero-glow"></div>
              <img src="/img/hero_dashboard.png" alt="Finio Dashboard Interface" class="hero-dashboard-img" fetchpriority="high" />
          </div>
          
          <p class="lead-text hero-subtitle">{{ $t('hero_subtitle') }}</p>
          
          <div class="hero-cta">
            <button class="btn btn-primary glow-effect" @click="$router.push('/subscriptions')">{{ $t('book_demo') }}</button>
          </div>
        </div>
      </section>

      <!-- Why Finio Section (New) -->
      <section class="why-finio-section">
        <div class="container">
           <div class="text-center mb-5 animate-fade-up">
              <h2 class="section-title">{{ $t('why_finio_title') }}</h2>
              <p class="lead-text section-subtitle">{{ $t('why_finio_subtitle') }}</p>
           </div>
           
           <div class="bento-grid why-finio-grid">
              <div class="why-card card-dark" v-for="(item, index) in whyFinioItems" :key="index">
                 <div class="card-icon-wrapper">
                    <i :class="item.icon"></i>
                 </div>
                 <h3>{{ $t(item.title) }}</h3>
                 <p>{{ $t(item.desc) }}</p>
              </div>
           </div>
        </div>
      </section>

      <!-- Value Pillars Section (New) -->
      <section class="pillars-section">
        <div class="container">
           <div class="bento-grid pillars-grid" style="grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1.5rem; margin-bottom: 4rem;">
              <BentoCard 
                v-for="(pillar, index) in pillars" 
                :key="index"
                class="animate-fade-up"
                :class="`delay-${(index + 1) * 100}`"
                :title="$t(pillar.title)" 
                :description="$t(pillar.description)"
                :icon="pillar.icon"
              />
           </div>
        </div>
      </section>

      <!-- Solutions Section (New Focus) -->
      <section class="solutions-section">
        <div class="container">
           <h2 class="animate-fade-up text-center mb-5">{{ $t('solutions_title') }}</h2>
           
           <div class="bento-grid">
              <BentoCard 
                v-for="(sol, index) in solutions" 
                :key="index"
                class="animate-fade-up"
                :class="`delay-${(index + 1) * 100}`"
                :title="$t(sol.title)" 
                :description="$t(sol.description)"
                :icon="sol.icon"
                :image="sol.image"
                :featured="index === 0"
              />
           </div>
        </div>
      </section>

      <!-- Modules Section (Categorized) -->
      <section class="modules-section">
        <div class="container">
          <div class="section-header text-center mb-5">
             <h2 class="animate-fade-up">{{ $t('our_modules_title') }}</h2>
          </div>

          <!-- Category Tabs -->
          <div class="tabs-container animate-fade-up delay-100">
             <button 
               v-for="(category, catIndex) in moduleCategories" 
               :key="catIndex"
               class="tab-btn"
               :class="{ active: activeCategory === catIndex }"
               @click="activeCategory = catIndex"
             >
               {{ $t(category.title) }}
             </button>
          </div>
          
          <div class="bento-grid modules-grid">
             <BentoCard 
               v-for="module in activeModules" 
               :key="module.title"
               :title="$t(module.title)" 
               :description="$t(module.description)"
               :icon="module.icon"
             />
             
             <!-- Dynamic "View All" link based on category could go here, or just a general one -->
             <div class="bento-card view-all-card animate-fade-up delay-200">
                <router-link to="/subscriptions" class="view-all-link">
                   <h3>{{ $t('products') }}</h3>
                   <i class="fas fa-arrow-right"></i>
                </router-link>
             </div>
          </div>
        </div>
      </section>

      <!-- Professional Services (Kept for completeness) -->
      <section class="services bg-noise">
         <div class="container">
            <h2 class="animate-fade-up text-center mb-5">{{ $t('professional_services_title') }}</h2>
            <div class="bento-grid">
              <BentoCard 
                 v-for="(service, index) in services"
                 :key="index"
                 class="animate-fade-up"
                 :title="$t(service.title)"
                 :description="$t(service.description)"
                 :icon="service.icon"
              />
            </div>
         </div>
      </section>

    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import BentoCard from '@/components/BentoCard.vue';

export default {
  name: 'HomePage',
  components: {
    BentoCard
  },
  setup() {
    const { t, locale } = useI18n();
    const router = useRouter();
    const currentDirection = computed(() => (locale.value === 'ar' ? 'rtl' : 'ltr'));

    const scrollToContact = () => {
      router.push('/contact');
    };

    onMounted(() => {
      // Re-trigger animations if needed or rely on global observer in App.vue
    });

    const solutions = ref([
      { icon: 'fas fa-utensils', title: 'solution_restaurant_title', description: 'solution_restaurant_desc', image: '/img/restaurants_image_one.png' },
      { icon: 'fas fa-shopping-bag', title: 'solution_retail_title', description: 'solution_retail_desc', image: '/img/store_retail_image.png' },
      { icon: 'fas fa-concierge-bell', title: 'solution_service_title', description: 'solution_service_desc', image: '/img/accountant_panel_image.png' },
    ]);

    const pillars = ref([
      { icon: 'fas fa-wifi', title: 'pillar_offline_title', description: 'pillar_offline_desc' },
      { icon: 'fas fa-file-invoice-dollar', title: 'pillar_integrity_title', description: 'pillar_integrity_desc' },
      { icon: 'fas fa-database', title: 'pillar_conflict_title', description: 'pillar_conflict_desc' },
      { icon: 'fas fa-lock', title: 'pillar_modular_title', description: 'pillar_modular_desc' },
    ]);

    const whyFinioItems = ref([
      { icon: 'fas fa-calculator', title: 'why_finio_card_1_title', desc: 'why_finio_card_1_desc' },
      { icon: 'fas fa-server', title: 'why_finio_card_2_title', desc: 'why_finio_card_2_desc' },
      { icon: 'fas fa-check-double', title: 'why_finio_card_3_title', desc: 'why_finio_card_3_desc' },
    ]);

    const activeCategory = ref(0);

    const moduleCategories = ref([
      {
        title: 'mod_sales_title',
        items: [
          { icon: 'fas fa-cash-register', title: 'mod_pos_title', description: 'mod_pos_desc' },
          { icon: 'fas fa-utensils', title: 'mod_kds_title', description: 'mod_kds_desc' },
          { icon: 'fas fa-motorcycle', title: 'mod_delivery_title', description: 'mod_delivery_desc' },
        ]
      },
      {
        title: 'mod_finance_title',
        items: [
          { icon: 'fas fa-file-invoice', title: 'mod_invoice_title', description: 'mod_invoice_desc' },
          { icon: 'fas fa-money-bill-wave', title: 'mod_cash_title', description: 'mod_cash_desc' },
          { icon: 'fas fa-receipt', title: 'mod_expense_title', description: 'mod_expense_desc' },
        ]
      },
      {
        title: 'mod_mgmt_title',
        items: [
          { icon: 'fas fa-boxes', title: 'mod_inventory_title', description: 'mod_inventory_desc' },
          { icon: 'fas fa-users', title: 'mod_crm_title', description: 'mod_crm_desc' },
          { icon: 'fas fa-user-shield', title: 'mod_admin_title', description: 'mod_admin_desc' },
        ]
      },
      {
        title: 'mod_hosp_title',
        items: [
          { icon: 'fas fa-layer-group', title: 'mod_floor_title', description: 'mod_floor_desc' },
          { icon: 'fas fa-hotel', title: 'mod_pms_title', description: 'mod_pms_desc' },
          { icon: 'fas fa-code', title: 'mod_api_title', description: 'mod_api_desc' },
        ]
      },
    ]);

    const activeModules = computed(() => moduleCategories.value[activeCategory.value].items);
    
    const services = ref([
      { icon: 'fas fa-code', title: 'custom_software_title', description: 'custom_software_description' },
      { icon: 'fas fa-microchip', title: 'hardware_software_title', description: 'hardware_software_description' },
      { icon: 'fas fa-handshake', title: 'consulting_title', description: 'consulting_description' },
    ]);

    const glassOpacity = ref(1);

    const offerBackground = computed(() => {
      const img = locale.value === 'ar' ? '/img/launch_membership_ar.webp' : '/img/launch_membership_en.webp';
      return `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.6)), url(${img})`;
    });

    const handleScroll = () => {
      const scrollY = window.scrollY;
      // Start fading immediately, disappear by 400px scroll
      const newOpacity = 1 - Math.min(scrollY / 400, 1);
      glassOpacity.value = newOpacity;
    };

    onMounted(() => {
      window.addEventListener('scroll', handleScroll);
    });

    return {
      t,
      currentDirection,
      scrollToContact,
      activeCategory,
      moduleCategories,
      activeModules,
      services,
      solutions,
      pillars,
      whyFinioItems,
      glassOpacity,
      offerBackground
    };
  },
};
</script>

<style scoped>
/* Home Container */
.home-container {
  overflow-x: hidden;
}

/* Hero Section */
.hero {
  position: relative;
  overflow: hidden;
  padding-top: 120px;
  padding-bottom: 80px;
  text-align: center;
}

.hero-content {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.hero-title {
  font-size: 4rem;
  line-height: 1.1;
  margin-bottom: 20px;
  max-width: 900px;
  background: linear-gradient(180deg, #fff 0%, rgba(255, 255, 255, 0.7) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
}

[dir="rtl"] .hero-title {
  line-height: 1.6 !important; /* Force taller line height for Arabic */
  padding-bottom: 15px; /* Ensure descenders aren't clipped */
}

/* Enhancing gradients for "text-gradient" class usage fallback */
.text-gradient {
  background: linear-gradient(135deg, #fff 30%, var(--neon-green) 100%);
  -webkit-background-clip: text;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.25rem;
  max-width: 600px;
  margin: 40px auto 40px auto;
  color: var(--secondary-text);
}

/* Dashboard Image Container */
.hero-image-container {
  position: relative;
  width: 100%;
  max-width: 1000px;
  margin-top: 40px;
  perspective: 1000px;
}

/* Hero Dashboard Frame (External Border) */
.hero-dashboard-img {
  width: 500px;
  max-width: 90vw;
  display: block;
  margin: 0 auto;
  border-radius: 20px;
  /* Double border effect mimicking the example */
  border: 8px solid rgba(20, 20, 20, 0.8);
  outline: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 
    0 0 0 1px rgba(0, 0, 0, 1), /* Inner black edge */
    0 20px 80px rgba(0, 0, 0, 0.6); /* Deep shadow */
  transform: rotateX(2deg);
  transition: transform 0.5s ease;
  background: #111; /* Dark bg for the frame itself */
}

.hero-image-container:hover .hero-dashboard-img {
  transform: rotateX(0deg) scale(1.02);
}

/* Aurora Backgrounds & Floating Glass */
.aurora-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.floating-glass {
  position: absolute;
  opacity: 0.8;
  filter: drop-shadow(0 10px 20px rgba(0,0,0,0.3));
  animation: floatGlass 6s ease-in-out infinite;
}

/* Positioning Glass Objects - Scattered like the example */
.glass-1 {
  top: 15%;
  left: 5%;
  width: 120px;
  animation-delay: 0s;
  transform: rotate(-15deg);
}

.glass-2 {
  top: 20%;
  right: 8%;
  width: 100px;
  animation-delay: -2s;
  transform: rotate(10deg);
  filter: blur(2px); /* Depth effect */
}

.glass-3 {
  bottom: 30%;
  left: 10%;
  width: 90px;
  animation-delay: -4s;
  animation-duration: 8s;
}

.glass-4 {
  top: 10%;
  left: 45%; /* Center-ish */
  width: 60px;
  opacity: 0.6;
  filter: blur(4px);
  animation-duration: 10s;
}

/* Foreground Glass - Large & Transparent */
.glass-fg-1 {
  /* Closest Element: Large, Blurry */
  top: -20%; 
  left: -10%;
  width: 400px; /* Increased size for "closest" feel */
  opacity: 0.8; 
  filter: blur(12px); /* Strong blur */
  z-index: 25;
  animation-duration: 18s;
  transform: rotate(-15deg);
  pointer-events: none;
  transition: opacity 0.1s ease-out; /* Smooth fade on scroll */
}

.glass-fg-2 {
  /* Further Element: Smaller, Less Blurry */
  bottom: -15%;
  right: -8%;
  width: 200px; /* Smaller size */
  opacity: 0.6;
  filter: blur(4px); /* Moderate blur */
  z-index: 25;
  animation-duration: 25s;
  animation-delay: -3s;
  transform: rotate(85deg); /* Different rotation */
  pointer-events: none;
  transition: opacity 0.1s ease-out;
}

@keyframes floatGlass {
  0% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
  100% { transform: translateY(0) rotate(0deg); }
}

.aurora-blob {
  position: absolute;
  filter: blur(80px);
  opacity: 0.6;
  border-radius: 50%;
  animation: floatAurora 10s infinite alternate cubic-bezier(0.4, 0, 0.2, 1);
}

.blob-1 {
  top: -10%;
  left: 20%;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(0, 255, 102, 0.15) 0%, transparent 70%);
}

.blob-2 {
  top: -20%;
  right: 10%;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(0, 200, 255, 0.15) 0%, transparent 70%); /* Blue hint */
  animation-delay: -5s;
}

.blob-3 {
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  width: 800px;
  height: 400px;
  background: radial-gradient(ellipse, rgba(138, 43, 226, 0.1) 0%, transparent 70%); /* Purple hint */
  animation-duration: 15s;
}

@keyframes floatAurora {
  0% { transform: translate(0, 0) scale(1); }
  100% { transform: translate(20px, -20px) scale(1.1); }
}

/* Glow Effect Button */
.glow-effect {
  box-shadow: 0 0 20px rgba(0, 255, 102, 0.3);
  transition: all 0.3s ease;
}

.glow-effect:hover {
  box-shadow: 0 0 40px rgba(0, 255, 102, 0.6);
  transform: translateY(-2px);
}

/* Home Mobile Specifics */
@media (max-width: 768px) {
    .hero-title {
        font-size: 2.5rem !important; /* Significantly smaller for mobile */
        margin-bottom: 15px !important;
    }

    .hero-subtitle {
        font-size: 1rem !important;
        padding: 0 15px; /* Add breathing room */
    }

    .glass-fg-1, .glass-fg-2 {
        opacity: 0.3 !important; /* Fade out foreground glass significantly */
        transform: scale(0.6) rotate(0deg) !important; /* Shrink them */
    }

    .hero-dashboard-img {
        transform: none !important; /* Remove 3D skew on mobile for clarity */
        border-width: 4px;
    }
    
    .hero-image-container:hover .hero-dashboard-img {
        transform: scale(1.02) !important;
    }

    .aurora-blob {
        opacity: 0.4; /* Dim background slightly */
    }
}

/* Why Finio Section */
.why-finio-section {
  padding: 80px 0;
  background-color: #050505;
}

.why-finio-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.why-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 2.5rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.why-card:hover {
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}

.card-icon-wrapper {
  font-size: 2rem;
  color: var(--primary);
  margin-bottom: 1.5rem;
  opacity: 0.9;
}

.why-card h3 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  font-weight: 700;
  color: white;
}

.why-card p {
  color: #aaa;
  line-height: 1.6;
  font-size: 1.05rem;
}

/* Offer Banner Styles */
.offer-banner-section {
  padding: 40px 0;
  position: relative;
  z-index: 20;
}

.offer-card {
  background-color: rgba(10, 10, 10, 0.8); /* Fallback */
  background-size: cover;
  background-position: center;
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 20px;
  padding: 60px 40px;
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  box-shadow: 0 0 30px rgba(0,0,0,0.5);
  transition: all 0.4s ease;
  
  /* Text Alignment Update */
  text-align: center; 
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px; /* Ensure height for bg image */
}

.offer-content-wrapper {
  max-width: 100%; 
  position: relative;
  z-index: 2;
}

[dir="rtl"] .offer-card {
  align-items: center; 
}

/* Ensure centering on mobile */
@media (max-width: 768px) {
  .offer-card {
    padding: 40px 20px;
  }
}

.offer-desc {
  color: #ccc;
  font-size: 1.2rem;
  margin-bottom: 30px;
  margin-left: auto; 
  margin-right: auto;
}

.glow-border {
  box-shadow: 0 0 15px rgba(255, 215, 0, 0.1), inset 0 0 20px rgba(0,0,0,0.8);
}

.offer-card:hover {
  transform: translateY(-5px);
  border-color: rgba(255, 215, 0, 0.5);
  box-shadow: 0 10px 40px rgba(255, 215, 0, 0.15);
}

.offer-badge {
  display: inline-block;
  background: rgba(255, 215, 0, 0.1);
  color: #FFD700;
  padding: 8px 16px;
  border-radius: 50px;
  border: 1px solid rgba(255, 215, 0, 0.3);
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 20px;
  box-shadow: 0 0 10px rgba(255, 215, 0, 0.1);
}

.offer-title {
  font-size: 2.5rem;
  margin-bottom: 15px;
  font-weight: 800;
}

.text-gradient-gold {
  background: linear-gradient(135deg, #FFF8E1 0%, #FFD700 50%, #B8860B 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 2px 10px rgba(0,0,0,0.3);
}

.offer-desc {
  color: #ccc;
  font-size: 1.2rem;
  margin-bottom: 30px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.pulse-btn {
  background: linear-gradient(45deg, #FFD700, #DAA520);
  color: #000;
  border: none;
  font-weight: 800;
  padding: 15px 40px;
  font-size: 1.1rem;
  position: relative;
  z-index: 30;
  cursor: pointer;
}

.pulse-btn:hover {
  background: linear-gradient(45deg, #FFF8E1, #FFD700);
  box-shadow: 0 0 30px rgba(255, 215, 0, 0.4);
}

.pulse-btn i {
  margin-left: 10px;
}

[dir="rtl"] .pulse-btn i {
  margin-left: 0;
  margin-right: 10px;
}

.relative-container {
  position: relative;
}

.glass-offer-1 {
  position: absolute;
  top: -60px;
  left: -100px;
  width: 250px; /* Larger size */
  opacity: 0.6; /* Increased transparency */
  z-index: 25;
  animation-duration: 7s;
  transform: rotate(-15deg);
  filter: blur(2px);
  pointer-events: none;
}

.glass-offer-2 {
  position: absolute;
  bottom: -80px;
  right: -80px;
  width: 280px; /* Larger size */
  opacity: 0.5;
  z-index: 25;
  animation-duration: 9s;
  animation-delay: -2s;
  transform: rotate(10deg);
  filter: blur(2px);
  pointer-events: none;
}

.glass-offer-3 {
  position: absolute;
  top: -90px;
  right: 10%;
  width: 180px;
  opacity: 0.4;
  z-index: 15; /* Behind card potentially if Z-index allows, but here it's sibling so it floats on top unless Z adjusted */
  animation-duration: 11s;
  transform: rotate(45deg);
  filter: blur(3px);
  pointer-events: none;
}

.glass-offer-4 {
  position: absolute;
  bottom: -40px;
  left: 20%;
  width: 150px;
  opacity: 0.3;
  z-index: 25;
  animation-duration: 13s;
  animation-delay: -5s;
  filter: blur(1px);
  pointer-events: none;
}

.glass-offer-5 {
  position: absolute;
  top: 40%;
  left: -120px;
  width: 200px;
  opacity: 0.5;
  z-index: 25;
  animation-duration: 8s;
  transform: rotate(-25deg);
  filter: blur(2px);
  pointer-events: none;
}

@media (max-width: 900px) {
  .glass-offer-1, .glass-offer-2, .glass-offer-3, .glass-offer-4, .glass-offer-5 {
    opacity: 0.3;
    transform: scale(0.6);
  }
}

/* Tabs Styles */
.tabs-container {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.tab-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 10px 20px;
  border-radius: 30px;
  color: var(--text-secondary);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-btn.active, .tab-btn:hover {
  background: var(--primary);
  color: #000;
  border-color: var(--primary);
  box-shadow: 0 0 15px rgba(0, 255, 102, 0.3);
}

@media (max-width: 600px) {
  .tabs-container {
    gap: 0.5rem;
  }
  .tab-btn {
    font-size: 0.9rem;
    padding: 8px 16px;
  }
}
</style>

